import { useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent, type MouseEvent } from 'react';
import { IncorrectPassphraseError, decryptContent } from '@realmery/realm-core';
import { entryIdOfLink, sanitizeEntryHtml } from '../security/sanitize.ts';

/**
 * Sanitized entry HTML; mention links (`realmery:entry/<id>`) navigate inside the app. A
 * Story Studio scene's manuscript is shown the same way, with its own words for no text.
 */
export function EntryContent({ html, empty = 'This entry has no text yet.' }: { html: string; empty?: string }) {
  const navigate = useNavigate();
  const { realmKey } = useParams({ strict: false });
  const safe = useMemo(() => sanitizeEntryHtml(html), [html]);

  function followMention(event: MouseEvent<HTMLDivElement>) {
    const link = (event.target as Element).closest('a');
    const href = link?.getAttribute('href') ?? '';
    const entryId = entryIdOfLink(href);
    if (entryId && realmKey) {
      event.preventDefault();
      void navigate({
        to: '/realm/$realmKey/entry/$entryId',
        params: { realmKey, entryId },
      });
    }
  }

  if (html.trim() === '') {
    return <p className="meta">{empty}</p>;
  }
  // The only HTML the app renders from a realm, and it is sanitized (security/sanitize.ts).
  return <div className="entry-content" data-find="" onClick={followMention} dangerouslySetInnerHTML={{ __html: safe }} />;
}

/**
 * A locked entry, readable with its passphrase - the desktop's "View (Unlock)": the text is
 * shown here only, and the entry stays locked in the realm.
 */
export function LockedContent({ blob }: { blob: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  async function unlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const field = event.currentTarget.elements.namedItem('passphrase') as HTMLInputElement;
    setWorking(true);
    setError('');
    try {
      setHtml(await decryptContent(blob, field.value));
    } catch (e) {
      setError(e instanceof IncorrectPassphraseError ? e.message : 'Could not unlock this entry.');
    } finally {
      field.value = '';
      setWorking(false);
    }
  }

  if (html !== null) {
    return (
      <section className="unlocked">
        <p className="meta">
          🔓 Shown with your passphrase. The entry stays locked.{' '}
          <button type="button" className="link-button" onClick={() => setHtml(null)}>
            Hide
          </button>
        </p>
        <EntryContent html={html} />
      </section>
    );
  }
  return (
    <form className="locked panel" onSubmit={unlock}>
      <p>🔒 This entry's text is locked with a passphrase.</p>
      <label>
        Passphrase
        <input name="passphrase" type="password" autoComplete="off" required />
      </label>
      <button type="submit" className="primary" disabled={working}>
        {working ? 'Unlocking…' : 'View'}
      </button>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
